import { ApiError } from '@/lib/http/client';
import type { CheckoutRejection } from './promo-code-input';
import type { PromoRejectReason } from './types';

const KNOWN_REASONS: ReadonlySet<string> = new Set<PromoRejectReason>([
  'unknown_code',
  'wrong_interval',
  'self_referral',
  'already_redeemed',
  'stripe_rejected',
  'referral_team_ineligible',
]);

function toKnownReason(reason: string | undefined): PromoRejectReason {
  return reason !== undefined && KNOWN_REASONS.has(reason) ? (reason as PromoRejectReason) : 'unknown_code';
}

/**
 * Checkout has two distinct rejection envelopes for a promo code (M4):
 * `PROMO_REJECTED` carries `details.reason` from our own validation;
 * `STRIPE_REJECTED` carries `details.stripeCode` from Stripe's second
 * validation at subscription-create and has no reason string of its own —
 * it is treated as equivalent to `reason: "stripe_rejected"`.
 *
 * Returns null for any error that isn't one of these two envelopes, so
 * callers only special-case checkout when the failure is actually about
 * the applied code.
 */
export function checkoutRejectionFromError(err: unknown, nonce: number): CheckoutRejection | null {
  if (!(err instanceof ApiError)) return null;
  if (err.code === 'STRIPE_REJECTED') {
    return { nonce, reason: 'stripe_rejected' };
  }
  if (err.code === 'PROMO_REJECTED') {
    const details = err.details as { reason?: string } | undefined;
    return { nonce, reason: toKnownReason(details?.reason) };
  }
  return null;
}
